import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Card,
  Flex,
  Row,
  Col,
  Alert,
  Upload
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  GlobalOutlined,
  InboxOutlined
} from '@ant-design/icons';
import {
  fetchPlatforms,
  createPlatform,
  updatePlatform,
  addPlatformRegion,
  deletePlatform,
  togglePlatformStatus,
  fetchRegions,
  importPlatforms
} from '../../utils/api';
import { downloadPlatformTemplate } from '../../utils/csvTemplate';

const { TextArea } = Input;
const { Option } = Select;

const PlatformManagement = () => {
  const [platforms, setPlatforms] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [managingRegionPlatform, setManagingRegionPlatform] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status: 1,
    regionIds: []
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState(null);
  // 批量导入
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  useEffect(() => {
    loadPlatforms();
    loadRegions();
  }, [currentPage]);

  const loadPlatforms = async () => {
    try {
      setLoading(true);
      const data = await fetchPlatforms(currentPage, pageSize);
      setPlatforms(data.list || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('加载平台数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async () => {
    try {
      const { list: data } = await fetchRegions(1, 100); // 获取所有地区
      setRegions(data || []);
    } catch (error) {
      console.error('加载地区数据失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingPlatform(null);
    setFormData({ name: '', code: '', description: '', status: 1, regionIds: [] });
    setShowModal(true);
  };

  const handleEdit = (platform) => {
    setEditingPlatform(platform);
    setFormData({
      name: platform.name,
      code: platform.code || '',
      description: platform.description || '',
      status: platform.status || 1,
      regionIds: platform.regionIds || []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingPlatform) {
        await updatePlatform(editingPlatform.platformId, formData);
        message.success('平台更新成功');
      } else {
        await createPlatform(formData);
        message.success('平台创建成功');
      }
      setShowModal(false);
      loadPlatforms();
    } catch (error) {
      console.error('保存平台数据失败:', error);
      message.error('保存失败: ' + error.message);
    }
  };

  const handleDelete = (platformId) => {
    setPlatformToDelete(platformId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePlatform(platformToDelete);
      message.success('平台删除成功');
      loadPlatforms();
    } catch (error) {
      console.error('删除平台失败:', error);
      message.error('删除失败: ' + error.message);
    } finally {
      setDeleteModalVisible(false);
      setPlatformToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setPlatformToDelete(null);
  };

  const handleToggleStatus = async (platform) => {
    try {
      const updatedPlatform = await togglePlatformStatus(platform.platformId);
      // 更新本地状态
      setPlatforms(platforms.map(p =>
        p.platformId === platform.platformId
          ? { ...p, status: updatedPlatform.status }
          : p
      ));
    } catch (error) {
      console.error('更新平台状态失败:', error);
      message.error('状态更新失败: ' + error.message);
    }
  };

  const [draftRegions, setDraftRegions] = useState([]);

  /* -------------------- 批量导入 -------------------- */
  const openImportModal = () => {
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportModalVisible(true);
  };

  const closeImportModal = () => {
    setImportModalVisible(false);
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportLoading(false);
  };

  const normalizeStatus = (val) => {
    const s = String(val || '').trim().toLowerCase();
    if (['1', 'enabled', 'active', 'true', '启用'].includes(s)) return 1;
    if (['0', 'disabled', 'inactive', 'false', '禁用'].includes(s)) return 0;
    return 1;
  };

  const parseCsv = (text) => {
    const errors = [];
    const rows = [];
    const stripBom = (s) => String(s || '').replace(/^\ufeff/, '');
    const lines = stripBom(String(text || '')).trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return { rows, errors };

    const firstCols = lines[0].split(',').map(c => stripBom(c).trim());
    const lcCols = firstCols.map(c => c.toLowerCase());
    const containsName = lcCols.some(c => c.includes('name') || c.includes('名称'));
    const containsCode = lcCols.some(c => c.includes('code') || c.includes('代码'));
    const hasHeader = (containsName && containsCode);
    const startIdx = hasHeader ? 1 : 0;
    
    for (let i = startIdx; i < lines.length; i++) {
      const raw = lines[i];
      const cols = raw.split(',').map(c => stripBom(c).trim());
      const name = cols[0] || '';
      const code = cols[1] || '';
      const description = cols[2] || '';
      const status = normalizeStatus(cols[3] || 'enabled');
      
      if (!name || !code) {
        errors.push(`第 ${i + 1} 行：name/code 不能为空`);
        continue;
      }
      rows.push({ name, code, description, status });
    }
    return { rows, errors };
  };

  const handleCsvTextChange = (e) => {
    const text = e.target.value;
    setCsvText(text);
    const { rows, errors } = parseCsv(text);
    setImportRows(rows);
    setImportErrors(errors);
  };

  const decodeCsvFile = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const tryDecode = (label) => {
        try {
          return new TextDecoder(label, { fatal: false }).decode(buffer);
        } catch (e) {
          return null;
        }
      };
      let text = tryDecode('utf-8');
      const hasReplacement = (s) => (s.match(/\uFFFD/g) || []).length > 0 || /����/.test(s);
      const hasChinese = (s) => /[\u4e00-\u9fa5]/.test(s);
      if (!text || hasReplacement(text)) {
        text = tryDecode('gbk') || tryDecode('gb18030') || text || '';
      }
      if ((!hasChinese(text)) && hasReplacement(text)) {
        text = tryDecode('utf-8') || text;
      }
      return text || '';
    } catch (err) {
      console.error('读取文件失败: ', err);
      message.error('读取文件失败');
      return '';
    }
  };

  const handleCsvFileUpload = (file) => {
    if (!file) return false;
    const isCsv = file.type === 'text/csv' || /\.csv$/i.test(file.name);
    if (!isCsv) {
      message.error('请上传 CSV 文件');
      return Upload.LIST_IGNORE || false;
    }
    const isLt10M = file.size <= 10 * 1024 * 1024;
    if (!isLt10M) {
      message.error('CSV 文件大小不能超过 10MB');
      return Upload.LIST_IGNORE || false;
    }
    (async () => {
      const text = await decodeCsvFile(file);
      setCsvText(text);
      const { rows, errors } = parseCsv(text);
      setImportRows(rows);
      setImportErrors(errors);
    })();
    return false;
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) {
      message.warning('没有可导入的数据');
      return;
    }
    setImportLoading(true);
    const result = await importPlatforms(importRows);
    setImportLoading(false);
    if (!result?.success) {
      message.error(result?.message || '导入失败');
      return;
    }
    message.success(result?.message || `导入成功（${importRows.length} 条）`);
    closeImportModal();
    await loadPlatforms();
  };

  // 打开弹窗时初始化
  const handleManageRegions = (platform) => {
    setManagingRegionPlatform(platform);
    setDraftRegions(platform.regions || []);
    setShowRegionModal(true);
  };

  // 添加/删除只改 draftRegions
  const handleAddRegion = (regionId) => {
    const added = regions.find(r => (r.id) === regionId);
    if (added && !draftRegions.some(r => r.id === added.id)) {
      setDraftRegions(prev => [...prev, { id: added.id, name: added.name }]);
    }
  };

  const handleRemoveRegion = (regionId) => {
    setDraftRegions(prev => prev.filter(r => r.id !== regionId));
  };

  // 使用 useMemo 缓存可添加地区，避免重复计算导致的多次重排
  const availableRegions = useMemo(() => {
    if (!managingRegionPlatform || !regions) return [];
    const associated = new Set(draftRegions.map(r => r.id));
    return regions.filter(region => !associated.has(region.id));
  }, [draftRegions, regions]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'platformId',
      key: 'platformId',
      width: 80,
    },
    {
      title: '平台名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: '关联地区',
      dataIndex: 'regions',
      key: 'regions',
      render: (regions) => (
        <div>
          {regions && regions.length > 0 ? (
            <Space wrap>
              {regions.map((region) => (
                <Tag key={region.id} color="blue">
                  {region.name}
                </Tag>
              ))}
            </Space>
          ) : (
            <span style={{ color: '#999' }}>未关联</span>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleManageRegions(record)}
          >
            编辑关联
          </Button>

          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type={record.status === 1 ? 'default' : 'primary'}
            size="small"
            icon={record.status === 1 ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.platformId)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 平台列表 */}
      <Card title="管理直播平台信息和地区关联">
        <Flex wrap gap="small" style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            新增平台
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={openImportModal}>
            批量导入
          </Button>
        </Flex>
        <Table
          columns={columns}
          dataSource={platforms}
          loading={loading}
          rowKey="platformId"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </Card>



      {/* 平台编辑模态框 */}
      <Modal
        title={editingPlatform ? '编辑平台' : '新增平台'}
        open={showModal}
        onOk={handleSave}
        onCancel={() => setShowModal(false)}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item label="平台名称" required>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="请输入平台名称"
            />
          </Form.Item>

          <Form.Item label="平台代码" required>
            <Input
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="请输入平台代码（如：youtube, twitch等）"
            />
          </Form.Item>

          <Form.Item label="描述">
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="请输入平台描述"
            />
          </Form.Item>

          <Form.Item label="状态" required>
            <Select
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value })}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 地区关联管理模态框 */}
      <Modal
        title={`编辑关联地区 - ${managingRegionPlatform?.name || ''}`}
        open={showRegionModal && managingRegionPlatform}
        onCancel={() => {
          setShowRegionModal(false);
          setManagingRegionPlatform(null);
          setDraftRegions([]);
        }}
        centered
        destroyOnHidden={false}
        maskClosable={false}
        styles={{ body: { minHeight: 360, maxHeight: 600, overflowY: 'auto' } }}
        footer={[
          <Button key="cancel" onClick={() => setShowRegionModal(false)}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={async () => {
              try {
                const regionIds = draftRegions.map(r => r.id);
                await addPlatformRegion(managingRegionPlatform.platformId, regionIds);
                message.success('关联地区已保存');
                loadPlatforms();
              } catch (e) {
                message.error('保存失败: ' + (e?.message || ''));
              } finally {
                setShowRegionModal(false);
                setManagingRegionPlatform(null);
                setDraftRegions([]);          // ← 清空草稿
              }
            }}
          >
            完成
          </Button>
        ]}
        width={800}
      >
        {managingRegionPlatform && (
          <>
            {/* 平台信息 */}
            <Alert
              message="平台信息"
              description={
                <Row gutter={16}>
                  <Col span={8}><strong>平台ID：</strong>{managingRegionPlatform.platformId}</Col>
                  <Col span={8}><strong>平台名称：</strong>{managingRegionPlatform.name}</Col>
                  <Col span={8}>
                    <strong>状态：</strong>
                    <Tag color={managingRegionPlatform.status === 1 ? 'green' : 'red'}>
                      {managingRegionPlatform.status === 1 ? '启用' : '禁用'}
                    </Tag>
                  </Col>
                </Row>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />

            {/* 当前关联地区 */}
            <Card title="当前关联地区" size="small" className="region-card-body">
              <div className="region-card-body">
                {draftRegions.length ? (
                  <Space wrap key="region-tags">   {/* ← 稳定 key */}
                    {draftRegions.map(r => (
                      <Tag
                        key={r.id}
                        color="blue"
                        closable
                        onClose={() => handleRemoveRegion(r.id)}
                      >
                        {r.name}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <div className="empty-box">
                    <GlobalOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>暂无关联地区</div>
                  </div>
                )}
              </div>
            </Card>

            {/* 可添加地区 */}
            <Card title="可添加地区" size="small" className="region-card-body">
              <div className="region-card-body">
                {availableRegions.length ? (
                  <Row gutter={[8, 8]}>
                    {availableRegions.map(r => (
                      <Col key={r.id} span={8}>
                        <Button
                          block
                          onClick={() => handleAddRegion(r.id)}
                        >
                          <Space>
                            <span>{r.name}</span>
                            <PlusOutlined style={{ color: '#52c41a' }} />
                          </Space>
                        </Button>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="empty-box">
                    <GlobalOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>所有地区已关联</div>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </Modal>

      {/* 删除确认模态框 */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="确定"
        cancelText="取消"
        okType="danger"
      >
        <p>确定要删除这个平台吗？</p>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入平台"
        open={importModalVisible}
        onCancel={closeImportModal}
        onOk={handleImportSubmit}
        okText="导入并保存"
        confirmLoading={importLoading}
        width={700}
      >
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Button type="link" onClick={downloadPlatformTemplate}>
              下载CSV模板
            </Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload.Dragger
              accept=".csv"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleCsvFileUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">拖放 CSV 文件到此处，或点击上传</p>
              <p className="ant-upload-hint">支持的格式: .csv（最大 10MB）</p>
            </Upload.Dragger>

            <div>
              <p style={{ color: '#555', marginTop: 8, marginBottom: 4 }}>CSV 文件格式要求：</p>
              <ul style={{ paddingLeft: 18, color: '#666' }}>
                <li>第一行为表头: name,code,description,status（可选）</li>
                <li>状态支持: 启用/禁用 或 enabled/disabled 或 1/0</li>
                <li>示例: YouTube,youtube,全球最大的视频分享平台,enabled</li>
              </ul>
            </div>

            <div>
              <p style={{ color: '#666', marginBottom: 8 }}>或直接粘贴CSV内容（包含或不包含表头）：name,code,description,status</p>
              <TextArea rows={6} value={csvText} onChange={handleCsvTextChange} placeholder="示例：\nYouTube,youtube,全球最大的视频分享平台,enabled\nTwitch,twitch,游戏直播平台,enabled" />
            </div>
          </Space>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Card size="small" title={`预览（有效 ${importRows.length} 条${importErrors.length ? `，错误 ${importErrors.length} 条` : ''}）`}>
            {importErrors.length > 0 && (
              <div style={{ color: '#c00', marginBottom: 8 }}>
                {importErrors.slice(0, 5).map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
                {importErrors.length > 5 && <div>更多错误已隐藏...</div>}
              </div>
            )}
            <Table
              size="small"
              columns={[
                { title: '平台名称', dataIndex: 'name' },
                { title: '平台代码', dataIndex: 'code' },
                { title: '描述', dataIndex: 'description' },
                { title: '状态', dataIndex: 'status', render: (s) => (s === 1 ? '启用' : '禁用') }
              ]}
              dataSource={importRows.map((r, i) => ({ ...r, _key: i }))}
              rowKey="_key"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      </Modal>
    </div>
  );
};

export default PlatformManagement;